import type { SlateBlock, TableBlock, TableCellBlock, TableRowBlock } from '@atlas/renderer';
import hotkey from '@atlas/tool/box/event/hotkey';
import type React from 'react';
import { Editor, Element, Transforms } from 'slate';
import areBlockLeavesEmpty from '@atlas/tool/box/slate/areBlockLeavesEmpty';
import areSamePoints from '@atlas/tool/box/slate/areSamePoints';
import createTextBlock from '@atlas/tool/box/slate/createTextBlock';
import deleteSelection from '@atlas/tool/box/slate/deleteSelection';
import getNode from '@atlas/tool/box/slate/getNode';
import isCalloutBlock from '@atlas/tool/box/slate/isCalloutBlock';
import isListBlock from '@atlas/tool/box/slate/isListBlock';
import isMultiLineTextBlock from '@atlas/tool/box/slate/isMultiLineTextBlock';
import isOneLineTextBlock from '@atlas/tool/box/slate/isOneLineTextBlock';
import isVoidBlock from '@atlas/tool/box/slate/isVoidBlock';
import matchBlockNodes from '@atlas/tool/box/slate/matchBlockNodes';
import changeEditorSelection from '@atlas/tool/box/slate/changeEditorSelection';

export default function handleEnterKeyDownEvent(editor: Editor, event: React.KeyboardEvent): boolean {
    if (!editor.selection) return false;

    const newLineHotkey = hotkey('enter', 'hidden');
    const superNewLineHotkey = hotkey('shift+enter', 'hidden');
    const isNewLine = newLineHotkey.matches(event);
    const isSuperNewLine = !isNewLine && superNewLineHotkey.matches(event);

    if (!isNewLine && !isSuperNewLine) return false;

    const [match] = matchBlockNodes(editor, { mode: 'lowest' });
    if (!match) return false;

    const [block, blockPath] = match;

    const topLevelBlock = blockPath.length > 1 ? getNode<SlateBlock>(editor, [blockPath[0]]) : block;
    if (!topLevelBlock) return false;

    if (isVoidBlock(block)) {
        event.preventDefault();

        if (isSuperNewLine) {
            Transforms.insertNodes(editor, createTextBlock(), {
                at: [blockPath[0]],
            });
            const focus = { offset: 0, path: [blockPath[0], 0] };
            changeEditorSelection(editor, { anchor: focus, focus });
        } else {
            deleteSelection(editor, editor.selection);
            Transforms.insertNodes(editor, createTextBlock());
        }

        return true;
    }

    if (block.type === 'table-cell') {
        event.preventDefault();

        deleteSelection(editor, editor.selection);

        if (isSuperNewLine) {
            Transforms.insertText(editor, '\n');
            return true;
        }

        const tableBlock = getNode<TableBlock>(editor, [blockPath[0]]);
        if (tableBlock) {
            if (blockPath[1] + 1 === tableBlock.children.length) {
                const columns = tableBlock.children[0].children.length;
                Transforms.insertNodes<TableRowBlock>(
                    editor,
                    {
                        type: 'table-row',
                        children: new Array(columns).fill(null).map<TableCellBlock>(() => ({
                            type: 'table-cell',
                            children: [{ text: '' }],
                        })),
                    },
                    { at: [blockPath[0], blockPath[1] + 1] },
                );
            }

            const focus = Editor.end(editor, [blockPath[0], blockPath[1] + 1, blockPath[2]]);
            changeEditorSelection(editor, { anchor: focus, focus });
        }

        return true;
    }

    if (block.type === 'list-item') {
        event.preventDefault();

        deleteSelection(editor, editor.selection);

        if (isSuperNewLine) {
            Transforms.insertText(editor, '\n');
            return true;
        }

        if (areBlockLeavesEmpty(block)) {
            Transforms.unwrapNodes(editor, {
                match: (n) => Element.isElement(n) && isListBlock(n),
                mode: 'lowest',
                split: true,
            });

            if (blockPath.length === 2) {
                Transforms.setNodes(editor, { type: 'paragraph' });
            }

            return true;
        }

        const before = Editor.string(editor, {
            anchor: Editor.start(editor, blockPath),
            focus: editor.selection.focus,
        });

        const after = Editor.string(editor, {
            anchor: Editor.end(editor, blockPath),
            focus: editor.selection.focus,
        });

        if (before.endsWith('\n')) Editor.deleteBackward(editor);
        if (after.startsWith('\n')) Editor.deleteForward(editor);

        if (before && after && before !== '\n' && after !== '\n') {
            Transforms.splitNodes(editor);
            return true;
        }

        Transforms.insertNodes(editor, {
            type: 'list-item',
            children: [{ text: '' }],
        });

        return true;
    }

    if (isMultiLineTextBlock(block) || (block.type === 'paragraph' && isCalloutBlock(topLevelBlock))) {
        event.preventDefault();

        deleteSelection(editor, editor.selection);

        const before = Editor.string(editor, {
            anchor: Editor.start(editor, blockPath),
            focus: editor.selection.focus,
        });

        const after = Editor.string(editor, {
            anchor: Editor.end(editor, blockPath),
            focus: editor.selection.focus,
        });

        if ((after && !after.startsWith('\n')) || !before.endsWith('\n\n')) {
            event.preventDefault();
            Transforms.insertText(editor, '\n');
            return true;
        }

        // Cursor is after 2 new lines and after it there is either empty line or no text

        // This removes the empty line in the second half if after is not empty
        if (after.startsWith('\n')) Editor.deleteForward(editor);

        // Split block with the paragraph (or add it after it if there was no more content)
        Transforms.insertNodes(editor, createTextBlock(), { at: [blockPath[0] + 1] });

        // Remove 2 last line breaks
        const endOfFirstHalf = Editor.end(editor, blockPath);
        changeEditorSelection(editor, {
            anchor: endOfFirstHalf,
            focus: endOfFirstHalf,
        });
        Editor.deleteBackward(editor);
        Editor.deleteBackward(editor);

        // Return focus to the new block
        const focus = { offset: 0, path: [blockPath[0] + 1, 0] };
        changeEditorSelection(editor, { anchor: focus, focus });
    }

    if (isOneLineTextBlock(block)) {
        const endBlockPoint = Editor.end(editor, [blockPath[0]]);

        if (!isNewLine && superNewLineHotkey.matches(event)) {
            event.preventDefault();
            deleteSelection(editor, editor.selection);
            Transforms.insertText(editor, '\n');
            return true;
        }

        if (areSamePoints(editor.selection.focus, endBlockPoint)) {
            event.preventDefault();
            deleteSelection(editor, editor.selection);
            Transforms.insertNodes(editor, createTextBlock());
            return true;
        }

        return true;
    }

    return false;
}

// #region Tests

if (import.meta.vitest) {
    const { describe, it } = import.meta.vitest;
    describe('handleEnterKeyDownEvent', async () => {
        it.todo('🤷🏼');
    });
}

// #endregion
